/**
 * spa-navigation.js
 * Lightweight SPA-style navigation for Shopify (Concept/RoarTheme).
 *
 * Supported page types: /products/*, /collections/*, /pages/*, / (homepage)
 *
 * What it does:
 *  - Intercepts clicks on internal links for supported page types
 *  - Prefetches pages on hover (100ms debounce)
 *  - Swaps #MainContent on click without a full page reload
 *  - Pushes real URL via history.pushState (preserves analytics page views)
 *  - Updates <title> and canonical <link>
 *  - Shows/hides the theme's native loading-bar element
 *  - Fires Shopify analytics page_viewed + product_viewed + collection_viewed events
 *  - Fires Klaviyo Viewed Product + trackViewedItem on SPA product transitions
 *  - Updates the sticky-product-navigator active state
 *  - Updates the theme's body template class
 *  - Dispatches page:loaded so theme animations re-run
 *  - Handles browser back/forward via popstate
 *  - Adds the navigated product to the theme's recently-viewed localStorage key (products only)
 *  - Re-initialises third-party widgets (Judge.me reviews on product pages, etc.) after swap
 *  - Custom redirects: configurable redirect map for both SPA and full page loads
 */

(function () {
  'use strict';

  // === Config ======================================================

  const PREFETCH_DELAY_MS  = 100;  // hover dwell time before prefetch fires
  const SWAP_FADE_DURATION  = 150;  // ms -- outgoing content fade-out
  const PRODUCTS_PATH_RE  = /^\/products\/[^?#]+/;  // kept for product-specific logic
  const SAME_ORIGIN  = window.location.origin;

  // Page types eligible for SPA navigation.
  // Add or remove patterns here to expand/restrict SPA coverage.
  // Each entry: { pattern: RegExp, type: string }
  const SPA_PAGE_TYPES = [
    { pattern: /^\/products\/[^?#]+/,     type: 'product'    },
    { pattern: /^\/collections(\/[^?#]*)?$/, type: 'collection' },
    { pattern: /^\/pages\/[^?#]+/,        type: 'page'       },
    { pattern: /^\/$/,                    type: 'homepage'   },
  ];

  // URLs to explicitly exclude from SPA even if they match a pattern above.
  // Examples: checkout, cart, account pages, external redirects.
  const SPA_EXCLUDE_RE = /^\/(cart|checkout|account|admin|search)/;

  // === Custom Redirects =============================================
  // Populated by the 'spa-redirects' Liquid snippet (window.__SPA_REDIRECTS).
  // Each entry: { match: RegExp, to: '/destination-path' }
  // Checked on both SPA transitions AND full page loads.
  const REDIRECTS = window.__SPA_REDIRECTS || [];

  // Preview mode: bypass redirects (set by view-map.liquid, persisted in sessionStorage)
  const PREVIEW_MODE = !!window.__PREVIEW_MODE;

  // === Redirect helper ===============================================

  /**
   * Reads a browser cookie by name. Used for redirect bypass checks.
   */
  function getCookie(name) {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  /**
   * Checks whether a given URL should be redirected.
   * Returns the target URL (absolute) if a redirect matches, or null.
   * Supports optional cookieName bypass: if entry.cookieName is set and that
   * cookie exists, the redirect is skipped (access already granted).
   * entry.to may be a string or a function(path) => string for dynamic destinations.
   */
  function getRedirectUrl(href) {
    try {
      const url = new URL(href, SAME_ORIGIN);
      if (url.origin !== SAME_ORIGIN) return null;
      const path = url.pathname;
      for (const entry of REDIRECTS) {
        if (entry.match.test(path)) {
          // Cookie bypass: if the access cookie is present, skip this redirect
          if (entry.cookieName && getCookie(entry.cookieName)) return null;
          const dest = typeof entry.to === 'function' ? entry.to(path) : entry.to;
          if (!dest) return null;
          if (/^https?:\/\//.test(dest)) return dest;
          return new URL(dest, SAME_ORIGIN).href;
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  // === State =======================================================

  const cache  = new Map();  // url -> { html, title, canonical }
  const inflight  = new Map();  // url -> Promise<cacheEntry>
  let  navigating  = false;  // guard against double-clicks
  let  prefetchTimer = null;

  // === Helpers =====================================================

  /**
   * Returns the SPA page type string ('product', 'collection', 'page', 'homepage')
   * if the URL is eligible for SPA navigation, or null if it should be a normal load.
   */
  function getSpaPageType(href) {
  try {
  const url = new URL(href, SAME_ORIGIN);
  if (url.origin !== SAME_ORIGIN) return null;
  if (SPA_EXCLUDE_RE.test(url.pathname)) return null;
  for (const entry of SPA_PAGE_TYPES) {
  if (entry.pattern.test(url.pathname)) return entry.type;
  }
  return null;
  } catch {
  return null;
  }
  }

  /** Quick boolean check — is this URL eligible for SPA nav? */
  function isSpaUrl(href) {
  return getSpaPageType(href) !== null;
  }

  /** Is this specifically a product URL? (used for product-only post-swap logic) */
  function isProductUrl(href) {
  return getSpaPageType(href) === 'product';
  }

  // Draft theme: map of path patterns to view= parameter values
  // Add new entries here as you build out more custom templates
   const VIEW_MAP = window.__VIEW_MAP || [];


  function normaliseUrl(href) {
  // Strip variant param and hash so cache hits are maximised on first visit
  const url = new URL(href, SAME_ORIGIN);
  url.hash = '';

  // Inject view= param for any URLs that require a custom template
  for (const entry of VIEW_MAP) {
  if (entry.match.test(url.pathname)) {
  if (!url.searchParams.has('view')) {
  url.searchParams.set('view', entry.view);
  }
  break;
  }
  }

  return url.toString();
  }

  function getLoadingBar() {
  return document.querySelector('loading-bar');
  }

  function showLoadingBar() {
  const bar = getLoadingBar();
  if (!bar) return;
  bar.hidden = false;
  // Reset any in-progress fade-out from Motion.animate
  bar.style.opacity = '1';
  }

  function hideLoadingBar() {
  // === Dispatch page loaded the theme's LoadingBar listens for this and fades itself out 
  document.dispatchEvent(new CustomEvent('page:loaded'));
  }

  // === Fetch & cache a page ========================================

  function fetchPage(url) {
  const normalised = normaliseUrl(url);

  if (cache.has(normalised))  return Promise.resolve(cache.get(normalised));
  if (inflight.has(normalised)) return inflight.get(normalised);

  const promise = fetch(normalised, { credentials: 'same-origin' })
  .then(res => {
  if (!res.ok) throw new Error(`SPA fetch failed: ${res.status} ${normalised}`);
  return res.text();
  })
  .then(html => {
  const parser  = new DOMParser();
  const doc  = parser.parseFromString(html, 'text/html');
  const pageType = getSpaPageType(normalised) || 'unknown';
  const entry  = {
  html:  doc.getElementById('MainContent')?.innerHTML ?? '',
  title:  doc.title,
  canonical: doc.querySelector('link[rel="canonical"]')?.getAttribute('href') ?? normalised,
  bodyClass: doc.body.className,
  pageType: pageType,
  // Product-specific data (null for non-product pages)
  productId:  pageType === 'product'
             ? (doc.querySelector('product-info')?.getAttribute('data-product-id') ?? null)
             : null,
  productTitle: pageType === 'product'
             ? (doc.querySelector('.jdgm-widget[data-product-title]')?.getAttribute('data-product-title')
             || doc.querySelector('product-info')?.getAttribute('data-product-title')
             || doc.querySelector('h1')?.textContent?.trim()
             || '')
             : '',
  productHandle: pageType === 'product' ? extractHandle(normalised) : null,
  };
  cache.set(normalised, entry);
  inflight.delete(normalised);
  return entry;
  })
  .catch(err => {
  inflight.delete(normalised);
  throw err;
  });

  inflight.set(normalised, promise);
  return promise;
  }

  function extractHandle(url) {
  const match = url.match(/\/products\/([^/?#]+)/);
  return match ? match[1] : null;
  }

  // === DOM swap ====================================================

  function applySwap(entry, url, preserveScroll = false) {
  const mainContent = document.getElementById('MainContent');
  if (!mainContent) return;

  // 1. Scroll to top instantly (skipped for sibling-product navigations e.g. variant-picker links)
  if (!preserveScroll) window.scrollTo(0, 0);

  // === Swap MainContent use theme's setInnerHTML so any script tags re-execute 
  if (window.theme?.HTMLUpdateUtility?.setInnerHTML) {
  window.theme.HTMLUpdateUtility.setInnerHTML(mainContent, entry.html);
  } else {
  // Fallback: manual script re-injection
  mainContent.innerHTML = entry.html;
  mainContent.querySelectorAll('script').forEach(old => {
  const s = document.createElement('script');
  Array.from(old.attributes).forEach(a => s.setAttribute(a.name, a.value));
  s.textContent = old.textContent;
  old.replaceWith(s);
  });
  }

  // 3. Update <title>
  document.title = entry.title;

  // 4. Update canonical link
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', entry.canonical);

  // 5. Update <body> template class (theme uses template-product etc.)
  // Only swap the template-* class, preserve everything else
  const newBodyClass = entry.bodyClass;
  const templateClassRe = /\btemplate-\S+/g;
  const newTemplateClass = (newBodyClass.match(templateClassRe) || []).join(' ');
  document.body.className = document.body.className.replace(templateClassRe, '').trim();
  if (newTemplateClass) document.body.classList.add(...newTemplateClass.split(' '));

  // 6. Push URL to history
  window.history.pushState({ spaNav: true, url }, '', url);

  // 7. Update sticky navigator active state
  updateNavigators(url);

  // 8. Fire analytics events (page_viewed always; product_viewed only for products)
  fireAnalyticsEvents(entry, url);

  // 9. Update recently-viewed in localStorage (product pages only)
  if (entry.pageType === 'product') {
  updateRecentlyViewed(entry.productId);
  setupVariantChangeListener();
  }

  // 10. Update window.meta.product so third-party scripts (Postscript,
  //     analytics) read the correct product on SPA navigations.
  updateShopifyMeta(entry);

  // === Signal load complete hides loading bar and re-triggers theme animations 
  hideLoadingBar();

  // Re-initialise third-party widgets after a short delay so the new
  // DOM is fully settled and any inline scripts from setInnerHTML have executed.
  // Product-specific widgets (Judge.me) only reinit on product pages.
  if (entry.pageType === 'product') {
  reinitThirdPartyWidgets(entry);
  }

  // Postscript: re-register dummyframe iframes and re-inject SDK
  // so phone-subscribe forms work after SPA navigation (all page types).
  reinitPostscript();

  // Mark that at least one SPA navigation has occurred so the
  // Postscript cart hook starts firing (skips on initial page load).
  hasSpaNavigated = true;
  }

  // === Navigate ====================================================

  async function navigate(url, pushState = true, preserveScroll = false) {
  if (navigating) return;

  // --- Redirect check (SPA transitions — skipped in preview mode) ---
  if (!PREVIEW_MODE) {
    const redirectTarget = getRedirectUrl(url);
    if (redirectTarget) {
      // Use replaceState so the redirected-from URL doesn't litter history
      window.location.replace(redirectTarget);
      return;
    }
  }

  navigating = true;

  const normalised = normaliseUrl(url);

  // Close all nav menus immediately (simulates real page load)
  closeAllNavs();

  // Show loading bar immediately
  showLoadingBar();

  // Fade out current main content slightly to signal transition
  const mainContent = document.getElementById('MainContent');
  if (mainContent) {
  mainContent.style.transition = `opacity ${SWAP_FADE_DURATION}ms ease`;
  mainContent.style.opacity  = '0.5';
  }

  try {
  // If already cached (prefetched), this resolves instantly
  const entry = await fetchPage(normalised);

  // Brief pause only if we faded (makes the fade visible if content was instant)
  await new Promise(r => setTimeout(r, SWAP_FADE_DURATION));

  // Restore opacity before swap so new content appears at full opacity
  if (mainContent) {
  mainContent.style.transition = '';
  mainContent.style.opacity  = '';
  }

  applySwap(entry, normalised, preserveScroll);
  } catch (err) {
  console.error('[spa-navigation] Navigation failed, falling back to full load:', err);
  // === Hard fallback just let the browser navigate normally ========
  window.location.href = url;
  } finally {
  navigating = false;
  }
  }

  // === Analytics ===================================================

  function fireAnalyticsEvents(entry, url) {
  try {
  // === Shopify Web Pixels / Trekkie fire page_viewed ===============
  if (window.Shopify?.analytics?.publish) {
  window.Shopify.analytics.publish('page_viewed', {
  url:  url,
  title: entry.title,
  });

  if (entry.productId) {
  window.Shopify.analytics.publish('product_viewed', {
  productId: entry.productId,
  url:  url,
  });
  }

  if (entry.pageType === 'collection') {
  window.Shopify.analytics.publish('collection_viewed', {
  url:  url,
  title: entry.title,
  });
  }
  }

  // Legacy Trekkie (some older Shopify setups)
  if (window.ShopifyAnalytics?.lib?.track) {
  window.ShopifyAnalytics.lib.track('Viewed Product', {
  product_id: entry.productId,
  });
  }

  // === Klaviyo: Viewed Product + trackViewedItem (SPA transitions) =
  if (entry.pageType === 'product') {
  fireKlaviyoProductEvents();
  }

  // === Google Analytics 4 (gtag) fire page_view ====================
  if (typeof window.gtag === 'function') {
  window.gtag('event', 'page_view', {
  page_title:  entry.title,
  page_location: url,
  page_path:  new URL(url).pathname,
  });
  }

  // Google Analytics Universal (ga.js / analytics.js)
  if (typeof window.ga === 'function') {
  window.ga('set', 'page', new URL(url).pathname);
  window.ga('send', 'pageview');
  }

  // Dispatch a custom event so any other scripts can hook in
  document.dispatchEvent(new CustomEvent('spa:navigated', {
  bubbles: true,
  detail: {
  url:  url,
  title:  entry.title,
  pageType: entry.pageType,
  productId: entry.productId,
  handle:  entry.productHandle,
  }
  }));
  } catch (e) {
  // Analytics should never break navigation
  console.warn('[spa-navigation] Analytics event error:', e);
  }
  }

  // === Klaviyo Product Events =======================================

  /**
   * Reads the #KlaviyoProductData JSON blob from the DOM (rendered by
   * main-product) and fires both klaviyo.track("Viewed Product") and
   * klaviyo.trackViewedItem(). Only called on SPA transitions —
   * Klaviyo's own snippet handles initial page loads.
   * Silently skips if Klaviyo or the data blob is absent.
   */
  function fireKlaviyoProductEvents() {
  try {
  var dataEl = document.getElementById('KlaviyoProductData');
  if (!dataEl) return;

  var data = JSON.parse(dataEl.textContent);
  if (!data || !data.ProductID) return;

  // Use the current browser URL so it captures any query params (e.g. ?view=)
  var currentUrl = window.location.href;

  // Wait for Klaviyo to be available (it loads async).
  // If already loaded, fire immediately; otherwise retry briefly.
  var attempts = 0;
  var maxAttempts = 20; // 20 × 250ms = 5s max wait

  function tryFire() {
  if (typeof window.klaviyo !== 'undefined' && typeof window.klaviyo.track === 'function') {
  // 1. Viewed Product event (used for flows, segments, etc.)
  window.klaviyo.track('Viewed Product', {
  Name:           data.Name,
  ProductID:      data.ProductID,
  SKU:            data.SKU,
  Categories:     data.Categories,
  ImageURL:       data.ImageURL,
  URL:            currentUrl,
  Brand:          data.Brand,
  Price:          data.Price,
  CompareAtPrice: data.CompareAtPrice
  });

  // 2. trackViewedItem (populates "recently viewed" feeds in Klaviyo)
  if (typeof window.klaviyo.trackViewedItem === 'function') {
  window.klaviyo.trackViewedItem({
  Title:      data.Name,
  ItemId:     data.ProductID,
  Categories: [data.Categories],
  ImageUrl:   data.ImageURL,
  Url:        currentUrl,
  Metadata: {
  Brand:          data.Brand,
  Price:          data.Price,
  CompareAtPrice: data.CompareAtPrice
  }
  });
  }

  console.log('[spa-navigation] Klaviyo Viewed Product fired:', data.Name);
  } else if (attempts < maxAttempts) {
  attempts++;
  setTimeout(tryFire, 250);
  }
  }

  tryFire();
  } catch (e) {
  console.warn('[spa-navigation] Klaviyo event error:', e);
  }
  }

  // === Recently Viewed =============================================

  function updateRecentlyViewed(productId) {
  if (!productId || !window.theme?.settings?.themeName) return;

  try {
  // Legacy key (product IDs only) — keep for backwards compat
  const key  = `${window.theme.settings.themeName}:recently-viewed`;
  const stored  = JSON.parse(window.localStorage.getItem(key) || '[]');
  const items  = new Set(stored);
  items.delete(parseInt(productId));
  const updated = [parseInt(productId), ...Array.from(items)].slice(0, 20);
  window.localStorage.setItem(key, JSON.stringify(updated));

  // Rich key (with variant data) — for Saved for You panel
  const richKey = `${window.theme.settings.themeName}:saved-for-you`;
  const richStored = JSON.parse(window.localStorage.getItem(richKey) || '[]');

  // Get current variant from URL or product form
  const urlParams = new URLSearchParams(window.location.search);
  const variantId = parseInt(urlParams.get('variant')) ||
    parseInt(document.querySelector('product-info input[name="id"]')?.value) || null;

  // Try to get rich data from the JSON block
  const dataEl = document.getElementById('SavedForYouData');
  if (dataEl) {
    const data = JSON.parse(dataEl.textContent);
    const variant = variantId
      ? data.variants.find(v => v.id === variantId)
      : data.variants[0];

    if (variant) {
      // Remove any existing entry for this product
      const filtered = richStored.filter(item => item.productId !== data.productId);

      // Prepend, cap at 20
      filtered.unshift({
        productId: data.productId,
        handle: data.handle,
        variantId: variant.id,
        title: data.title,
        url: data.url + '?variant=' + variant.id,
        variantTitle: variant.title,
        options: variant.options,
        price: variant.price,
        compareAtPrice: variant.compareAtPrice,
        discountedPrice: variant.discountedPrice || '',
        strikethroughPrice: variant.strikethroughPrice || '',
        available: variant.available,
        image: variant.image,
        shipsIn: variant.shipsIn,
        savedAt: Date.now()
      });

      window.localStorage.setItem(richKey, JSON.stringify(filtered.slice(0, 20)));
    }
  }
  } catch (e) {
  // localStorage may be unavailable
  }
  }

  // Track current variant:change listener so we can clean up on SPA nav
  let _variantChangeCleanup = null;

  function setupVariantChangeListener() {
    // Clean up previous listener if any
    if (_variantChangeCleanup) {
      _variantChangeCleanup();
      _variantChangeCleanup = null;
    }

    const dataEl = document.getElementById('SavedForYouData');
    if (!dataEl) return;

    const productInfoEl = document.querySelector('product-info[form]');
    const formId = productInfoEl ? productInfoEl.getAttribute('form') : null;
    const target = (formId && document.forms[formId]) || null;
    if (!target) return;

    const themeName = window.theme?.settings?.themeName;
    if (!themeName) return;

    function onVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;
      try {
        const data = JSON.parse(dataEl.textContent);
        const richKey = `${themeName}:saved-for-you`;
        const richStored = JSON.parse(localStorage.getItem(richKey) || '[]');
        const v = data.variants.find(dv => dv.id === variant.id);
        if (!v) return;
        const filtered = richStored.filter(item => item.productId !== data.productId);
        filtered.unshift({
          productId: data.productId, handle: data.handle, variantId: v.id,
          title: data.title, url: data.url + '?variant=' + v.id,
          variantTitle: v.title, options: v.options,
          price: v.price, compareAtPrice: v.compareAtPrice,
          discountedPrice: v.discountedPrice || '', strikethroughPrice: v.strikethroughPrice || '',
          available: v.available, image: v.image, shipsIn: v.shipsIn,
          savedAt: Date.now()
        });
        localStorage.setItem(richKey, JSON.stringify(filtered.slice(0, 20)));
      } catch (e) { /* silent */ }
    }

    target.addEventListener('variant:change', onVariantChange);
    _variantChangeCleanup = () => target.removeEventListener('variant:change', onVariantChange);
  }

  // Set up on initial page load (for non-SPA first visit)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupVariantChangeListener);
  } else {
    setupVariantChangeListener();
  }

  // === Third-party widget re-initialization ========================
  // After an SPA swap the DOM is replaced but third-party scripts that
  // ran on the original page load don't know new content has arrived.
  // We call their public re-init APIs here.

  // === Update window.meta.product ==================================
  // Shopify sets window.meta.product via content_for_header on initial
  // page load. Third-party scripts like Postscript read this object for
  // product context (page views, tracking). On SPA navigations, the
  // <head> is never re-rendered, so window.meta.product goes stale.
  //
  // Fix: main-product renders a #ShopifyMetaProduct JSON blob with the
  // same structure. After swap we parse it and overwrite the global.

  function updateShopifyMeta(entry) {
    try {
      if (entry.pageType !== 'product') {
        // Clear product meta when navigating away from a product page
        // so scripts don't think we're still on a product.
        if (window.meta && window.meta.product) {
          window.meta.product = undefined;
        }
        return;
      }

      var dataEl = document.getElementById('ShopifyMetaProduct');
      if (!dataEl) return;

      var productMeta = JSON.parse(dataEl.textContent);
      if (!productMeta || !productMeta.id) return;

      // Ensure the global meta object exists
      window.meta = window.meta || {};
      window.meta.product = productMeta;

      // Also update ShopifyAnalytics.meta if it exists (some scripts read from there)
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
        window.ShopifyAnalytics.meta.product = productMeta;
      }

      console.log('[spa-navigation] window.meta.product updated:', productMeta.id, productMeta.vendor);
    } catch (e) {
      console.warn('[spa-navigation] Failed to update window.meta.product:', e);
    }
  }

  // === Postscript add-to-cart event ================================
  // The Postscript SE recommended calling window.postscript.event()
  // manually on add-to-cart since their native listener breaks after
  // SPA navigations. We subscribe to the theme's cartUpdate pubsub
  // event which fires after every successful add-to-cart AJAX call.
  //
  // IMPORTANT: Only fire after an SPA navigation has occurred — on
  // the initial page load Postscript's own listener handles it fine.
  //
  // Ref: https://developers.postscript.io/docs/javascript-sdk-api-reference#windowpostscriptevent

  let hasSpaNavigated = false; // flipped true on first SPA swap

  function initPostscriptCartHook() {
    if (!window.theme?.pubsub?.subscribe) return;

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (data) {
      // Only fire on add-to-cart (source: product-form), not on quantity changes etc.
      if (!data || data.source !== 'product-form') return;

      // Skip on the initial page load — Postscript handles that natively
      if (!hasSpaNavigated) return;

      if (!window.postscript || typeof window.postscript.event !== 'function') return;

      try {
        var productMeta = (window.meta && window.meta.product) ? window.meta.product : null;
        var variantId = data.productVariantId;

        // Find the variant that was just added from the cart response
        var cartItem = null;
        if (data.cart && data.cart.items) {
          for (var i = 0; i < data.cart.items.length; i++) {
            if (String(data.cart.items[i].variant_id) === String(variantId) ||
                String(data.cart.items[i].id) === String(variantId)) {
              cartItem = data.cart.items[i];
              break;
            }
          }
        }

        // Find variant details from meta
        var variantMeta = null;
        if (productMeta && productMeta.variants) {
          for (var v = 0; v < productMeta.variants.length; v++) {
            if (String(productMeta.variants[v].id) === String(variantId)) {
              variantMeta = productMeta.variants[v];
              break;
            }
          }
        }

        // Build the payload per Postscript SDK docs
        var shopIdEl = document.getElementById('ps__shop_id');
        var payload = {
          shop_id:       shopIdEl ? shopIdEl.value : undefined,
          url:           window.location.href,
          search_params: Object.fromEntries(new URL(window.location.href).searchParams),
          page_type:     'product',
          referrer:      document.referrer || '',
          resource: {
            category:       productMeta ? productMeta.type : (cartItem ? cartItem.product_type : ''),
            product_title:  cartItem ? cartItem.product_title : '',
            variant_title:  variantMeta ? variantMeta.public_title : (cartItem ? cartItem.variant_title : ''),
            price_in_cents: cartItem ? cartItem.price : (variantMeta ? variantMeta.price : 0),
            resource_id:    productMeta ? productMeta.id : (cartItem ? cartItem.product_id : null),
            resource_type:  'product',
            sku:            variantMeta ? variantMeta.sku : (cartItem ? cartItem.sku : null),
            variant_id:     parseInt(variantId, 10),
            vendor:         productMeta ? productMeta.vendor : (cartItem ? cartItem.vendor : ''),
          }
        };

        window.postscript.event('add_to_cart', payload);
        console.log('[spa-navigation] Postscript add_to_cart event fired:', payload.resource.resource_id, 'variant:', variantId);
      } catch (e) {
        console.warn('[spa-navigation] Postscript cart event error:', e);
      }
    });
  }

  // Initialise the cart hook once on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPostscriptCartHook);
  } else {
    initPostscriptCartHook();
  }

  // Track whether Judge.me's core snippet has finished its initial setup.
  // The jdgm.doneSetup event fires once Judge.me is fully ready.
  let judgeMeReady = false;
  document.addEventListener('jdgm.doneSetup', () => { judgeMeReady = true; });

  function reinitThirdPartyWidgets(entry) {
    // Judge.me: reload all review widgets after SPA content swap.
    // Per Judge.me support: use ONLY jdgmCacheServer.reloadAll() (not
    // jdgm.initializeWidgets(), which causes a duplicate widget render).
    //
    // IMPORTANT: The review widget lives in a global drawer OUTSIDE
    // #MainContent, so SPA swaps don't touch it. We must manually update
    // data-id and data-product-title so Judge.me fetches the correct
    // product's reviews. We also reset caches and clear the cache server
    // so reloadAll() makes a fresh API call.

    function doJudgeMeReload() {
      try {
        // 0. Update data attributes on ALL Judge.me widget containers
        //    (covers widgets both inside and outside #MainContent —
        //    the review widget lives in a global drawer)
        if (entry && entry.productId) {
          document.querySelectorAll('.jdgm-widget[data-id], .jdgm-preview-badge[data-id]').forEach(widget => {
            widget.setAttribute('data-id', entry.productId);
            if (entry.productTitle) {
              widget.setAttribute('data-product-title', entry.productTitle);
            }
            // Remove the "done" classes so Judge.me re-processes this widget
            widget.classList.remove('jdgm--done-setup-widget', 'jdgm--done-setup');
          });
        }

        // 1. Reset Judge.me's "done" flag (mirrors InstantClick compat hook)
        if (window.jdgm) {
          jdgm._doneSetup = null;

          // 2. Refresh jdgm.caches so it references the NEW DOM elements
          if (jdgm.$ && typeof jdgm.$ === 'function') {
            jdgm.caches = {
              $revWidgets:  jdgm.$('.jdgm-review-widget, .jdgm-carousel-wrapper'),
              $prevBadges:  jdgm.$('.jdgm-preview-badge'),
              $allStars:    jdgm.$('[class*="jdgm-star"]'),
              $body:        jdgm.$(document.body),
            };
          }
        }

        // 3. Clear the cache server's internal data so reloadAll()
        //    fetches fresh reviews for the new product
        if (window.jdgmCacheServer) {
          if (jdgmCacheServer.cache) jdgmCacheServer.cache = {};
          if (jdgmCacheServer.data)  jdgmCacheServer.data = {};
          if (jdgmCacheServer._cache) jdgmCacheServer._cache = {};
          if (typeof jdgmCacheServer.clearCache === 'function') {
            jdgmCacheServer.clearCache();
          }
          if (typeof jdgmCacheServer.clear === 'function') {
            jdgmCacheServer.clear();
          }

          // 4. Reload — now forces a fresh fetch from Judge.me's API
          if (typeof jdgmCacheServer.reloadAll === 'function') {
            jdgmCacheServer.reloadAll();
          }
        }

        // Fix lazy images and neutralise href="#" links after Judge.me renders async
        setTimeout(() => {
          fixJudgeMeImages();
          neutraliseJudgeMeLinks();
        }, 500);
      } catch (e) {
        console.warn('[spa-navigation] Judge.me reinit error:', e);
      }
    }

    if (window.jdgmCacheServer && typeof jdgmCacheServer.reloadAll === 'function') {
      if (judgeMeReady) {
        doJudgeMeReload();
      } else {
        document.addEventListener('jdgm.doneSetup', () => doJudgeMeReload(), { once: true });
      }
    }
  }

  // === Postscript SDK re-initialisation ==============================
  // Postscript's SDK loads once on initial page load and binds to
  // existing forms. After an SPA swap the new DOM contains fresh form
  // markup, but the SDK doesn't know about it. Additionally, iframes
  // injected via innerHTML don't register their `name` attribute in the
  // browser's browsing-context list, so target="dummyframe" opens a new
  // tab instead of posting silently to the hidden iframe.
  //
  // Fix:
  //  1. Re-register any dummyframe iframes by cloning them via DOM APIs.
  //  2. Remove the existing SDK script and re-inject it so it processes
  //     the new page's forms from scratch.

  function reinitPostscript() {
    try {
      // --- Step 1: Fix iframe target registration -----------------------
      // Iframes injected via innerHTML don't register their `name` in the
      // browsing context. Clone and replace them via proper DOM insertion
      // so the browser picks up the name attribute.
      document.querySelectorAll('iframe[name="dummyframe"]').forEach(function (oldIframe) {
        var newIframe = document.createElement('iframe');
        newIframe.width  = '0';
        newIframe.height = '0';
        newIframe.style.border = '0';
        newIframe.name   = 'dummyframe';
        newIframe.id     = oldIframe.id || '';
        oldIframe.parentNode.replaceChild(newIframe, oldIframe);
      });

      // --- Step 2: Restore stripped form attributes ----------------------
      // The theme's setInnerHTML sanitises the swapped HTML, stripping
      // inline event handlers (onsubmit) and the target attribute.
      // Re-apply them via DOM APIs so the form posts to the hidden iframe
      // and shows the success message without a redirect.
      var psForm = document.querySelector('#ps__subscribe-form form');
      if (psForm) {
        // Restore target so the POST goes to the hidden iframe
        if (!psForm.getAttribute('target')) {
          psForm.setAttribute('target', 'dummyframe');
        }

        // Attach submit handler via addEventListener (survives sanitisation)
        // Only attach once — guard with a data attribute
        if (!psForm.hasAttribute('data-ps-spa-bound')) {
          psForm.setAttribute('data-ps-spa-bound', '1');
          psForm.addEventListener('submit', function () {
            var sentMsg = document.getElementById('ps__sent-message');
            var formWrap = document.getElementById('ps__subscribe-form');
            if (sentMsg) sentMsg.style.display = 'block';
            if (formWrap) formWrap.style.display = 'none';
          });
        }

        console.log('[spa-navigation] Postscript form patched (target + submit handler)');
      }

      // --- Step 3: Remove and re-inject the Postscript SDK --------------
      var existingScript = document.getElementById('ps-sdk-loader');
      if (existingScript) {
        // Capture the src before removal so we can re-inject the same URL
        var sdkSrc = existingScript.src;
        existingScript.parentNode.removeChild(existingScript);

        // Brief delay to let cleanup settle before re-injecting
        setTimeout(function () {
          var script  = document.createElement('script');
          script.type = 'text/javascript';
          script.id   = 'ps-sdk-loader';
          script.src  = sdkSrc;
          document.getElementsByTagName('head')[0].appendChild(script);
          console.log('[spa-navigation] Postscript SDK re-injected');
        }, 100);
      }
    } catch (e) {
      console.warn('[spa-navigation] Postscript reinit error:', e);
    }
  }

  // === Judge.me Image Force-Loader ==================================
  // Judge.me's lazy loader (jdgm--loading class + data-src) relies on
  // an IntersectionObserver that frequently fails to fire, especially
  // after SPA navigations. This leaves gallery thumbnails, review
  // photos, and badge images stuck in a loading/blank state.
  //
  // Fix: Force-swap data-src → src on ALL Judge.me images/links that
  // carry the jdgm--loading class. For the "Verified by Shop" badge
  // we optionally swap to a self-hosted copy on Shopify's CDN.
  //
  // SETUP (badge only): Upload verified-by-shop_light.svg to
  //   Settings → Files and paste the CDN URL below. Leave empty to
  //   keep using Judge.me's imgix URL for the badge.

  const JUDGEME_BADGE_CDN_URL = 'https://cdn.shopify.com/s/files/1/0743/9905/6165/files/verified-by-shop_light.svg'; // ← UPDATE with your Shopify Files URL

  /**
   * Force-load every Judge.me element that is stuck in jdgm--loading.
   * Handles: <img data-src>, <a> wrappers with jdgm--loading, and
   * the verification badge image.
   */
  function fixJudgeMeImages(root) {
    const scope = root || document;

    // 1. All <img> elements with data-src inside Judge.me widgets
    scope.querySelectorAll('.jdgm--loading img[data-src], img.jdgm--loading[data-src]').forEach(img => {
      const dataSrc = img.getAttribute('data-src');
      if (dataSrc && !img.src.includes(dataSrc.split('?')[0])) {
        img.src = dataSrc;
      }
      img.classList.remove('jdgm--loading');
    });

    // 2. Parent <a> / <div> wrappers that carry jdgm--loading
    //    (e.g. .jdgm-rev__pic-link, .jdgm-gallery__thumbnail-wrapper)
    scope.querySelectorAll('.jdgm--loading').forEach(el => {
      el.classList.remove('jdgm--loading');
      // Also fix any child images that weren't caught above
      el.querySelectorAll('img[data-src]').forEach(img => {
        const dataSrc = img.getAttribute('data-src');
        if (dataSrc && !img.src.includes(dataSrc.split('?')[0])) {
          img.src = dataSrc;
        }
      });
    });

    // 3. Badge images — swap to self-hosted CDN if configured
    if (JUDGEME_BADGE_CDN_URL) {
      scope.querySelectorAll('img.jdgm-rev__verification-badge-img').forEach(img => {
        img.src = JUDGEME_BADGE_CDN_URL;
        img.removeAttribute('data-src');
        img.classList.remove('jdgm--loading');
      });
    }
  }

  // Run once on initial page load (after Judge.me renders)
  document.addEventListener('jdgm.doneSetup', () => fixJudgeMeImages());

  // MutationObserver catches images rendered after SPA swaps or
  // asynchronous Judge.me widget updates (pagination, etc.)
  const jdgmImageObserver = new MutationObserver(mutations => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        // Check if the added node or its children have stuck images
        if (node.classList?.contains('jdgm--loading') || node.querySelector?.('.jdgm--loading')) {
          fixJudgeMeImages(node.nodeType === 1 && node.parentElement ? node.parentElement : node);
        }
      }
      // Also catch class changes (jdgm--loading added to existing nodes)
      if (m.type === 'attributes' && m.attributeName === 'class' && m.target.classList?.contains('jdgm--loading')) {
        fixJudgeMeImages(m.target.parentElement || m.target);
      }
    }
  });
  jdgmImageObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

  // === Judge.me "Write a Review" Button Fix ===========================
  // The write-rev-link is an <a href="#">. Clicking it changes the URL
  // hash (e.g. from #reviews-section to #), which triggers the drawer's
  // hashchange listener, closing it and scrolling the page.
  //
  // Fix: Strip the href entirely. Judge.me's click handler is bound via
  // addEventListener, not the href, so it still works. We use a
  // MutationObserver to catch Judge.me (re-)adding href="#" at any point.

  function neutraliseJudgeMeLinks(scope) {
    (scope || document).querySelectorAll('a.jdgm-write-rev-link[href="#"]').forEach(link => {
      link.removeAttribute('href');
      link.setAttribute('role', 'button');
      link.style.cursor = 'pointer';
    });
  }

  // Run immediately + on Judge.me setup
  neutraliseJudgeMeLinks();
  document.addEventListener('jdgm.doneSetup', () => neutraliseJudgeMeLinks());

  // Observer: Judge.me may (re-)add href="#" after async renders,
  // pagination, or SPA reloads. Watch for it and strip immediately.
  new MutationObserver(mutations => {
    for (const m of mutations) {
      // Catch href="#" being set on existing write-rev-links
      if (m.type === 'attributes' && m.attributeName === 'href') {
        const el = m.target;
        if (el.matches?.('a.jdgm-write-rev-link') && el.getAttribute('href') === '#') {
          el.removeAttribute('href');
          el.setAttribute('role', 'button');
          el.style.cursor = 'pointer';
        }
      }
      // Catch new write-rev-link nodes being added
      if (m.type === 'childList') {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('a.jdgm-write-rev-link[href="#"]')) {
            node.removeAttribute('href');
            node.setAttribute('role', 'button');
            node.style.cursor = 'pointer';
          }
          // Check descendants
          if (node.querySelectorAll) neutraliseJudgeMeLinks(node);
        }
      }
    }
  }).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href']
  });

  // Safety net: if a click somehow fires before the observer strips href,
  // prevent the browser from following it
  document.addEventListener('click', function(e) {
    const link = e.target.closest('a.jdgm-write-rev-link[href="#"]');
    if (link) e.preventDefault();
  }, true);

  // First-click fix: Our CSS forces the button visible before Judge.me
  // initialises it. The first click just sets aria-expanded="false" (init),
  // and only the second click actually opens the form. Fix: if aria-expanded
  // isn't set yet, let Judge.me init on this event, then auto-fire a second
  // click so the form opens on what the user perceives as one click.
  // (Safe now that href="#" has been stripped — no hash change risk.)
  document.addEventListener('click', function(e) {
    const link = e.target.closest('.jdgm-write-rev-link');
    if (!link || link.classList.contains('close-form-btn')) return;

    if (!link.hasAttribute('aria-expanded')) {
      requestAnimationFrame(() => link.click());
    }
  });

  // Prevent mobile auto-focus scroll when form opens,
  // and smooth-scroll within the drawer to show the form
  document.addEventListener('click', function(e) {
    const link = e.target.closest('.jdgm-write-rev-link');
    if (!link) return;

    // When "Cancel Review" is clicked, Judge.me destroys and recreates
    // the "Write a Review" link — often with href="#" re-added and
    // aria-expanded removed. Watch for it to reappear and fix it.
    if (link.classList.contains('close-form-btn')) {
      const poll = setInterval(() => {
        const newLink = document.querySelector('.jdgm-write-rev-link:not(.close-form-btn)');
        if (newLink) {
          clearInterval(poll);
          neutraliseJudgeMeLinks();
        }
      }, 50);
      // Safety: stop polling after 2s
      setTimeout(() => clearInterval(poll), 2000);
      return;
    }

    setTimeout(() => {
      // Blur any auto-focused input
      const formWrapper = document.querySelector('.jdgm-form-wrapper');
      if (formWrapper) {
        const focused = formWrapper.querySelector(':focus');
        if (focused) focused.blur();
      }
      // Smooth scroll the drawer to show the form
      const drawer = link.closest('drawer-element');
      if (!drawer) return;
      const scrollable = drawer.querySelector('.drawer__scrollable');
      const form = drawer.querySelector('.jdgm-form');
      if (scrollable && form) {
        const formTop = form.getBoundingClientRect().top - scrollable.getBoundingClientRect().top + scrollable.scrollTop;
        scrollable.scrollTo({ top: formTop - 50, behavior: 'smooth' });
      }
    }, 200);
  });

  // === Global Drawer Scroll Lock =====================================
  // Two scroll sources when drawers are open:
  //  1. Judge.me's openForm() uses jQuery .animate({ scrollTop }) which
  //     sets body.scrollTop directly via jQuery's fx.tick animation loop.
  //  2. Judge.me auto-focuses form inputs, triggering native browser
  //     scroll-into-view that bypasses all JS overrides.
  // Fix: intercept both body.scrollTop setter AND focus() while any
  // drawer is open.

  let drawerScrollLockActive = false;

  // Patch body.scrollTop to suppress jQuery scroll animations while drawer is open
  const scrollTopDesc = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollTop');
  Object.defineProperty(document.body, 'scrollTop', {
    get() { return scrollTopDesc.get.call(this); },
    set(v) {
      if (drawerScrollLockActive) return; // suppress
      scrollTopDesc.set.call(this, v);
    },
    configurable: true
  });
  // Also patch html element (some browsers use documentElement for page scroll)
  Object.defineProperty(document.documentElement, 'scrollTop', {
    get() { return scrollTopDesc.get.call(this); },
    set(v) {
      if (drawerScrollLockActive) return; // suppress
      scrollTopDesc.set.call(this, v);
    },
    configurable: true
  });

  // Patch focus() to force preventScroll while drawer is open
  const origFocus = HTMLElement.prototype.focus;
  HTMLElement.prototype.focus = function(opts) {
    if (drawerScrollLockActive) {
      return origFocus.call(this, Object.assign({}, opts, { preventScroll: true }));
    }
    return origFocus.call(this, opts);
  };

  // Watch for any drawer-element opening/closing
  new MutationObserver(mutations => {
    const drawerChanged = mutations.some(m => m.target.tagName === 'DRAWER-ELEMENT');
    if (!drawerChanged) return;
    drawerScrollLockActive = !!document.querySelector('drawer-element:not([hidden])');
  }).observe(document.body, {
    attributes: true,
    attributeFilter: ['hidden'],
    subtree: true
  });

  // === Sticky Navigator Visibility Active State ====================

  /**
  * Evaluates whether a navigator should be visible on the given pathname,
  * mirroring the Liquid show_navigator logic in sticky-product-navigator.liquid.
  *
  * Rules (in priority order, matching the Liquid):
  *  1. data-show-on-all-pages="true" -> always show
  *  2. data-show-on-all-products="true" AND pathname starts with /products/ -> show
  *  3. data-specific-pages contains a matching path fragment -> show
  *  4. None of the above configured -> show by default (Liquid fallback)
  *  5. Otherwise -> hide
  */
  // Shared visibility evaluator used by both navigators and announcement bars
  function shouldSectionShow(el, pathname, search) {
  const showOnAllPages  = el.getAttribute('data-show-on-all-pages') === 'true';
  const showOnAllProducts  = el.getAttribute('data-show-on-all-products') === 'true';
  // Support both old (data-specific-pages) and new (data-show-on-specific-pages) attribute names
  const showOnSpecificPages = el.getAttribute('data-show-on-specific-pages') || el.getAttribute('data-specific-pages') || '';
  const hideOnSpecificPages = el.getAttribute('data-hide-on-specific-pages') || '';

  // Entries that contain "=" match against URL query params; the rest
  // match against the pathname. e.g. "preview=52413298999589" matches
  // any URL with ?preview=52413298999589, "preview" (no value) matches
  // any URL where the param is set to anything. Useful for gating an
  // announcement bar on a preview/share link without exposing it on
  // the rest of the site.
  const matchEntry = (entry, path, params) => {
  const trimmed = entry.trim();
  if (trimmed === '') return false;
  if (trimmed.indexOf('=') !== -1) {
  const eqIdx = trimmed.indexOf('=');
  const key = trimmed.substring(0, eqIdx);
  const val = trimmed.substring(eqIdx + 1);
  if (val === '') return params.has(key);
  return params.get(key) === val;
  }
  const stripped = trimmed.replace(/^\//, '');
  return path.replace(/^\//, '').includes(stripped);
  };

  const params = new URLSearchParams(search || '');
  const matchesList = (list) => list.split(',').some(p => matchEntry(p, pathname, params));

  // Determine base visibility
  let visible = false;

  if (showOnAllPages) {
  visible = true;
  } else if (showOnAllProducts && PRODUCTS_PATH_RE.test(pathname)) {
  visible = true;
  } else if (showOnSpecificPages.trim() !== '') {
  visible = matchesList(showOnSpecificPages);
  } else {
  // No show rules configured - show by default
  visible = true;
  }

  // Hide rules always take priority
  if (visible && hideOnSpecificPages.trim() !== '') {
  if (matchesList(hideOnSpecificPages)) visible = false;
  }

  return visible;
  }

  // Back-compat alias
  const shouldNavigatorShow = shouldSectionShow;

  function updateNavigators(url) {
  const urlObj = new URL(url, SAME_ORIGIN);
  const pathname = urlObj.pathname;
  const search = urlObj.search;

  // === Sticky product navigators ===================================
  document.querySelectorAll('.sticky-product-navigator').forEach(nav => {
  // 0. Clear yielding state — will be re-applied by controlled navs if needed
  nav.classList.remove('spn--yielding');

  // 1. Show/hide based on page visibility rules
  const visible = shouldSectionShow(nav, pathname, search);
  nav.classList.toggle('spa-hidden', !visible);

  // 2. Update data-current-path
  nav.setAttribute('data-current-path', pathname);

  // 3. Update active links (path-based) within this navigator
  nav.querySelectorAll('.sticky-product-navigator__link').forEach(link => {
  const linkUrl   = new URL(link.href, SAME_ORIGIN);
  const linkPath  = linkUrl.pathname;
  const linkHash  = linkUrl.hash;
  const rawHref   = link.getAttribute('href') || '';

  // Hash-only links (href="#fever-102") or same-page hash links should NOT
   // get path-active — they live on the same page, so pathname always matches.
   // Let the active_in_section IntersectionObserver handle their active state.
   // EXCEPTION: hash links that ALSO have data-active-on-paths are path-matched
   // and should keep their active state (e.g. SHOP PADDLES → #page-top on the
   // shop-paddles page).
  const hasPathMatching = link.hasAttribute('data-active-on-paths');
  const isHashLink = (rawHref.startsWith('#') || (linkHash && linkPath === pathname)) && !hasPathMatching;

  if (isHashLink) {
    // Clear path-active; section observer will set section-active independently
    link.classList.remove('sticky-product-navigator__link--active');
    link.classList.remove('sticky-product-navigator__link--path-active');
    link.classList.remove('sticky-product-navigator__link--section-active');
    link.classList.remove('sticky-product-navigator__link--path-demoted');
  } else {
    const activePaths = link.hasAttribute('data-active-on-paths')
    ? link.getAttribute('data-active-on-paths').split(',').map(p => p.trim())
    : [];

    const isActive = activePaths.some(p => pathname.includes(p.replace(/^\//, '')))
    || pathname === linkPath;

    link.classList.toggle('sticky-product-navigator__link--active', isActive);
    link.classList.toggle('sticky-product-navigator__link--path-active', isActive);
    // Clear section-active and demoted state since DOM has changed
    link.classList.remove('sticky-product-navigator__link--section-active');
    link.classList.remove('sticky-product-navigator__link--path-demoted');
  }
  });

  // 4. Reinitialize section visibility observer (old targets replaced by SPA swap)
  if (nav._sectionObserver) {
    nav._sectionObserver.disconnect();
    nav._sectionObserver = null;
  }

  const showInSelector = nav.getAttribute('data-show-in-section');
  const state = nav._spnState;

  if (showInSelector) {
    if (!visible) {
      console.log('[SPN-SPA] Skipping observers — nav is path-hidden on', pathname);
      if (state) state.setSectionVisible(false);
      else {
        nav.classList.add('spn--section-hidden');
        nav.classList.remove('spn--section-visible');
      }
    } else {
      // Reset to hidden, then re-observe with fresh DOM targets
      if (state) state.setSectionVisible(false);
      else {
        nav.classList.add('spn--section-hidden');
        nav.classList.remove('spn--section-visible');
      }

      requestAnimationFrame(() => {
        const targets = document.querySelectorAll(showInSelector);
        console.log('[SPN-SPA] show_in_section re-init:', showInSelector, '→', targets.length, 'target(s)');
        if (!targets.length) return;

        const visibleSet = new Set();
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) visibleSet.add(entry.target);
            else visibleSet.delete(entry.target);
          });
          const anyVisible = visibleSet.size > 0;
          if (state) state.setSectionVisible(anyVisible);
          else {
            nav.classList.toggle('spn--section-hidden', !anyVisible);
            nav.classList.toggle('spn--section-visible', anyVisible);
          }
        }, { rootMargin: '0px', threshold: 0 });

        targets.forEach(t => observer.observe(t));
        nav._sectionObserver = observer;
      });
    }
  }

  // 5. Re-check scroll threshold (page may have scrolled to top after SPA nav)
  if (state && state.checkScrollThreshold) {
    state.checkScrollThreshold();
  }

  // 6. Reinitialize scroll-spy for active links (targets are fresh DOM after SPA swap)
  if (state && state.initScrollSpy) {
    requestAnimationFrame(() => {
      state.initScrollSpy();
    });
  }

  // 7. Scroll the pill list to center the active link
  requestAnimationFrame(() => {
    const navList = nav.querySelector('.sticky-product-navigator__list');
    if (!navList) return;
    const activeLink = navList.querySelector('.sticky-product-navigator__link--active');
    if (!activeLink) return;
    const listRect = navList.getBoundingClientRect();
    const linkRect = activeLink.getBoundingClientRect();
    let offset = linkRect.left - listRect.left - (listRect.width / 2) + (linkRect.width / 2);
    // Clamp: don't scroll past boundaries
    const maxScrollLeft = navList.scrollWidth - navList.clientWidth;
    const newScrollLeft = navList.scrollLeft + offset;
    if (newScrollLeft < 0) offset = -navList.scrollLeft;
    if (newScrollLeft > maxScrollLeft) offset = maxScrollLeft - navList.scrollLeft;
    if (Math.abs(offset) > 5) {
      navList.scrollBy({ left: offset, behavior: 'smooth' });
    }
  });
  });

  // === Announcement bar items - per-block visibility ===============
  // Use spa-hidden class (not inline style) so the theme's page:loaded
  // reinitialisation cannot wipe the hidden state the way it can inline styles.
  document.querySelectorAll('.announcement-bar-item').forEach(item => {
  // Only manage items that have at least one visibility attribute
  if (!item.hasAttribute('data-show-on-all-pages') && !item.hasAttribute('data-show-on-specific-pages') && !item.hasAttribute('data-hide-on-specific-pages')) return;
  const visible = shouldSectionShow(item, pathname, search);
  item.classList.toggle('spa-hidden', !visible);
  });

  // === Generic section display gating (FRI-287) ====================
  // Any element opting in with .section-display-gate + visibility attrs
  // (e.g. custom-feature-carousel) is shown/hidden by the same rules as
  // announcement bars, including ?preview= URL-param entries. Reusable —
  // future sections just add the class + attrs (no JS change needed).
  document.querySelectorAll('.section-display-gate').forEach(el => {
  if (!el.hasAttribute('data-show-on-all-pages') && !el.hasAttribute('data-show-on-specific-pages') && !el.hasAttribute('data-hide-on-specific-pages')) return;
  const visible = shouldSectionShow(el, pathname, search);
  el.classList.toggle('spa-hidden', !visible);
  });
  }

  // Run updateNavigators on initial load so visibility rules that depend
  // on URL query params (e.g. announcement bars gated on ?preview=<id>)
  // resolve on first paint. Liquid renders based on path only.
  function runInitialNavUpdate() {
  try { updateNavigators(window.location.href); } catch (e) {}
  }
  if (document.readyState !== 'loading') runInitialNavUpdate();
  else document.addEventListener('DOMContentLoaded', runInitialNavUpdate);

  // Preserve ?preview=<value> across SPA navigations. When sessionStorage
  // has a preview flag set, any nav-resulting URL that doesn't include
  // ?preview gets the param re-appended via history.replaceState — so
  // shareable preview links stay sticky as the user clicks around the
  // site without each link needing to carry the param.
  //
  // After updating the URL, re-run updateNavigators against the new URL
  // so visibility rules that match on URL params (e.g. an announcement
  // gated on ?preview=<id>) pick up the param. Otherwise updateNavigators
  // would have run with the param-less URL during SPA nav, before
  // page:loaded fires this handler.
  function preservePreviewParam() {
  try {
  const ids = sessionStorage.getItem('previewVariantIds');
  const hidden = sessionStorage.getItem('previewHiddenVariants');
  const desired = ids ? ids : (hidden === '1' ? 'hidden' : null);
  if (!desired) return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('preview') === desired) return;
  params.set('preview', desired);
  const newUrl = window.location.pathname + '?' + params.toString() + window.location.hash;
  window.history.replaceState(window.history.state, '', newUrl);
  try { updateNavigators(window.location.href); } catch (e) {}
  } catch (e) {}
  }
  document.addEventListener('page:loaded', preservePreviewParam);
  if (document.readyState !== 'loading') preservePreviewParam();
  else document.addEventListener('DOMContentLoaded', preservePreviewParam);

  // === Prefetch ====================================================

  function schedulePreFetch(url) {
  cancelPreFetch();
  prefetchTimer = setTimeout(() => {
  const normalised = normaliseUrl(url);
  if (!cache.has(normalised) && !inflight.has(normalised)) {
  fetchPage(normalised).catch(() => {}); // silent -- prefetch failure is harmless
  }
  }, PREFETCH_DELAY_MS);
  }

  function cancelPreFetch() {
  if (prefetchTimer) {
  clearTimeout(prefetchTimer);
  prefetchTimer = null;
  }
  }
  // === Close all navigation menus ============================================
  // Simulates what a real page load does: closes desktop mega menus,
  // desktop dropdown details, and the mobile drawer.
  // Uses the theme's own custom-element APIs so transitions and state stay clean.

  function closeAllNavs() {
    // 1. Desktop: close any open details-mega (mega menu panels)
    document.querySelectorAll('details[is="details-mega"][open]').forEach(d => {
      d.open = false; // triggers theme's transitionOut() and removes body.with-mega
    });

    // 2. Desktop: close any open details-dropdown (regular nav dropdowns)
    document.querySelectorAll('details[is="details-dropdown"][open]').forEach(d => {
      d.open = false;
    });

    // 3. Mobile: close the menu drawer via the theme's hide() API
    const menuDrawer = document.querySelector('menu-drawer');
    if (menuDrawer && menuDrawer.open) {
      menuDrawer.hide();
    }

    // 4. Close the cart drawer on SPA navigation
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.open) {
      cartDrawer.hide();
    }
  }

  // === Event Delegation ============================================

  function getSpaLink(target) {
  // Walk up from the clicked element to find an <a> pointing to an SPA-eligible page
  const anchor = target.closest('a[href]');
  if (!anchor) return null;

  const href = anchor.getAttribute('href');
  if (!href) return null;

  // Skip links that opt out
  if (anchor.hasAttribute('data-no-spa')) return null;

  // Skip new-tab, download links
  if (anchor.target === '_blank' || anchor.hasAttribute('download')) return null;

  // Skip modifier keys (user wants to open in new tab etc.)
  return isSpaUrl(href) ? anchor : null;
  }

  // === Click handler global delegation on document =================
  document.addEventListener('click', function (e) {
  // Ignore if modifier key held (Cmd/Ctrl + click = new tab intent)
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  if (e.button !== 0) return; // not left click

  const anchor = getSpaLink(e.target);
  if (!anchor) return;

  const url = anchor.href; // absolute URL

  // Don't SPA-navigate to the page we're already on
  if (normaliseUrl(url) === normaliseUrl(window.location.href)) return;

  e.preventDefault();
  cancelPreFetch();

  // If the click originated inside a .variant-picker (sibling product swatch),
  // preserve the current scroll position — it's effectively a same-page variant swap.
  // Exception: the Model pills (product_variations block, e.g. AURA PRO ↔ AURA)
  // navigate between products with different page layouts, so they scroll to top
  // like any other page navigation (FRI-363).
  const pickerEl = e.target.closest('.variant-picker');
  const preserveScroll = !!pickerEl && !pickerEl.classList.contains('variant-picker--product-variations');
  navigate(url, true, preserveScroll);
  });

  // === Mega-menu summary[data-link] SPA handler =================
  // The theme's details-mega custom element navigates via
  //   window.location.href = summary.getAttribute('data-link')
  // inside onSummaryClicked(), which bypasses SPA entirely.
  // This capture-phase handler intercepts BEFORE the theme's handler
  // fires, routing the navigation through SPA instead.
  //
  // After the SPA navigation, we suppress hover-reopening of the mega
  // menu for HOVER_COOLDOWN_MS so the user doesn't get the panel
  // immediately sliding back open while the page is swapping.

  const HOVER_COOLDOWN_MS = 2500;

  document.addEventListener('click', function (e) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (e.button !== 0) return;

    var summary = e.target.closest('summary[data-link]');
    if (!summary) return;

    var dataLink = summary.getAttribute('data-link');
    if (!dataLink || !isSpaUrl(dataLink)) return;

    // Only intercept on desktop (hover-trigger menus) — the theme only
    // does the data-link navigation for non-touch hover menus
    var details = summary.closest('details[is]');
    if (!details) return;
    var trigger = details.getAttribute('trigger');
    if (trigger !== 'hover') return;

    // Prevent the theme's onSummaryClicked from firing
    e.preventDefault();
    e.stopImmediatePropagation();

    // Resolve to absolute URL
    var url = new URL(dataLink, SAME_ORIGIN).href;
    if (normaliseUrl(url) === normaliseUrl(window.location.href)) return;

    // 1. Close the mega menu immediately (it's open from the hover)
    details.open = false;

    // 2. Suppress hover-reopening: disable pointer events so mouseenter
    //    can't reach the theme's detectHover handler at all.
    details.style.pointerEvents = 'none';

    // 3. Also switch trigger to 'click' as a belt-and-suspenders backup
    details.setAttribute('trigger', 'click');

    // 4. Restore after cooldown
    setTimeout(function () {
      details.style.pointerEvents = '';
      details.setAttribute('trigger', 'hover');
    }, HOVER_COOLDOWN_MS);

    navigate(url);
  }, true); // ← CAPTURE phase — fires before the theme's bubble-phase handler

  // === Mega-menu hover delay override ===============================
  // The theme's DetailsMega sets megaHoverDelay = 300ms by default.
  // Increase it so the menu doesn't open too eagerly on drive-by hovers.
  const MEGA_HOVER_DELAY_MS = 500;

  function setMegaHoverDelay() {
    document.querySelectorAll('details[is="details-mega"]').forEach(function(el) {
      if (typeof el.megaHoverDelay !== 'undefined') {
        el.megaHoverDelay = MEGA_HOVER_DELAY_MS;
      }
    });
  }

  // Set on load and after any SPA navigation (new mega menus won't exist,
  // but the header persists so this mainly catches initial load).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setMegaHoverDelay);
  } else {
    setMegaHoverDelay();
  }

  // === Hover prefetch ==============================================
  document.addEventListener('mouseover', function (e) {
  const anchor = getSpaLink(e.target);
  if (anchor) schedulePreFetch(anchor.href);
  });

  document.addEventListener('mouseout', function (e) {
  const anchor = getSpaLink(e.target);
  if (anchor) cancelPreFetch();
  });

  // === Touch prefetch on touchstart (fires before click on mobile) =
  document.addEventListener('touchstart', function (e) {
  const anchor = getSpaLink(e.target);
  if (anchor) schedulePreFetch(anchor.href);
  }, { passive: true });

  // === Back / Forward (popstate) ===================================

  // Same-page anchor clicks (href="#x") also fire popstate. Track the URL of the
  // current entry (minus hash) so those are left to the browser's native jump
  // instead of re-swapping the page and scrolling to the top.
  const stripHash = (href) => href.split('#')[0];
  let lastEntryUrl = stripHash(window.location.href);
  ['pushState', 'replaceState'].forEach((method) => {
  const original = window.history[method];
  window.history[method] = function () {
  const result = original.apply(this, arguments);
  lastEntryUrl = stripHash(window.location.href);
  return result;
  };
  });

  window.addEventListener('popstate', function (e) {
  const url = window.location.href;

  const previousEntryUrl = lastEntryUrl;
  lastEntryUrl = stripHash(url);
  if (lastEntryUrl === previousEntryUrl) return; // hash-only change

  // Only intercept if it's an SPA-eligible URL; let browser handle everything else
  if (!isSpaUrl(url)) return;

  // Close navs on back/forward too
  closeAllNavs();

  // If we have it cached, swap silently; otherwise do a full load
  const normalised = normaliseUrl(url);
  if (cache.has(normalised)) {
  const entry = cache.get(normalised);

  showLoadingBar();

  const mainContent = document.getElementById('MainContent');
  if (mainContent) {
  mainContent.style.transition = `opacity ${SWAP_FADE_DURATION}ms ease`;
  mainContent.style.opacity  = '0.5';
  }

  setTimeout(() => {
  if (mainContent) {
  mainContent.style.transition = '';
  mainContent.style.opacity  = '';
  }
  // Use applySwap but don't push another history entry
  _applySwapNoHistory(entry, normalised);
  }, SWAP_FADE_DURATION);
  } else {
  // === Not cached do a real load (e.g. user navigated back to an SPA page 
  // we never prefetched)
  window.location.reload();
  }
  });

  // Internal version of applySwap that skips pushState (used for popstate)
  function _applySwapNoHistory(entry, url) {
  const mainContent = document.getElementById('MainContent');
  if (!mainContent) return;

  window.scrollTo(0, 0);

  if (window.theme?.HTMLUpdateUtility?.setInnerHTML) {
  window.theme.HTMLUpdateUtility.setInnerHTML(mainContent, entry.html);
  } else {
  mainContent.innerHTML = entry.html;
  }

  document.title = entry.title;
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute('href', entry.canonical);

  updateNavigators(url);
  fireAnalyticsEvents(entry, url);
  if (entry.pageType === 'product') {
  updateRecentlyViewed(entry.productId);
  setupVariantChangeListener();
  }
  updateShopifyMeta(entry);
  hideLoadingBar();

  if (entry.pageType === 'product') {
  reinitThirdPartyWidgets(entry);
  }

  reinitPostscript();
  hasSpaNavigated = true;
  }

  // === Disable theme's instant-page.js for SPA-eligible links ======
  // instant-page.js also prefetches on hover. Since we're doing that ourselves,
  // we mark SPA-eligible links with a data-no-instant attribute which instant-page
  // respects, so we avoid double-fetching.
  //
  // We do this lazily via a MutationObserver so it works on dynamically added links.

  function tagInstantPageLinks() {
  document.querySelectorAll('a[href]').forEach(a => {
  if (isSpaUrl(a.href) && !a.hasAttribute('data-no-instant')) {
  a.setAttribute('data-no-instant', '');
  }
  });
  }

  // Run once on load, then observe DOM changes
  if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tagInstantPageLinks);
  } else {
  tagInstantPageLinks();
  }

  const mutationObserver = new MutationObserver(tagInstantPageLinks);
  mutationObserver.observe(document.body, { childList: true, subtree: true });

  // === Prefetch links visible in viewport on page load =============
  // Give the page a moment to settle, then prefetch the first few visible product
  // links so common next-page navigations are instant even without hover.

  function prefetchVisibleLinks() {
  if (!('IntersectionObserver' in window)) return;

  let prefetchCount = 0;
  const MAX_AUTO_PREFETCH = 4;

  const observer = new IntersectionObserver((entries, obs) => {
  entries.forEach(entry => {
  if (!entry.isIntersecting) return;
  if (prefetchCount >= MAX_AUTO_PREFETCH) { obs.disconnect(); return; }

  const anchor = entry.target;
  const normalised = normaliseUrl(anchor.href);
  if (!cache.has(normalised) && !inflight.has(normalised)) {
  prefetchCount++;
  fetchPage(normalised).catch(() => {});
  }

  obs.unobserve(anchor);
  });
  }, { rootMargin: '0px 0px 200px 0px' });

  document.querySelectorAll('a[href]').forEach(a => {
  if (isSpaUrl(a.href)) observer.observe(a);
  });
  }

  // Delay to not compete with critical resources
  if (document.readyState === 'complete') {
  setTimeout(prefetchVisibleLinks, 1000);
  } else {
  window.addEventListener('load', () => setTimeout(prefetchVisibleLinks, 1000));
  }

  // === Neutralise Rebuy SmartCart ====================================
  // Rebuy is injected store-wide via a ScriptTag (through content_for_header)
  // and cannot be disabled per-theme. This block prevents SmartCart from ever
  // opening so the native cart drawer can take over.

  (function blockRebuySmartCart() {
    const noop = () => {};

    function neutralise(rebuy) {
      if (!rebuy?.SmartCart) return;
      rebuy.SmartCart.show = noop;
      rebuy.SmartCart.open = noop;
      rebuy.SmartCart.toggle = noop;
    }

    // If Rebuy is already loaded, neutralise immediately
    if (window.Rebuy) neutralise(window.Rebuy);

    // Trap future assignments to window.Rebuy so we catch it on load
    let _rebuy = window.Rebuy;
    Object.defineProperty(window, 'Rebuy', {
      get() { return _rebuy; },
      set(val) {
        _rebuy = val;
        neutralise(val);
      },
      configurable: true
    });

    // Safety net: Rebuy may re-attach SmartCart methods after init,
    // so keep patching for a few seconds after page load
    let attempts = 0;
    const interval = setInterval(() => {
      if (window.Rebuy) neutralise(window.Rebuy);
      if (++attempts >= 20) clearInterval(interval);
    }, 500);
  })();

  // === Expose public API for debugging / external hooks ============

  window.spaNavigation = {
  navigate,
  prefetch: url => fetchPage(normaliseUrl(url)).catch(() => {}),
  clearCache: () => cache.clear(),
  cache,
  fireKlaviyoProductEvents,
  };

})();